import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

const money = (v) => `$${Number(v || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Per-quote cost / margin check in the runner review panel — same numbers + warnings as the manual Pricing panel.
export default function QuoteCostCheck({ api, childId, rowKey, refreshKey, onOkChange }) {
  const [s, setS] = useState(null);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!childId) return undefined;
    api.get(`/quote-summary/${childId}`).then(({ data }) => { if (!cancelled) setS(data); }).catch(() => { if (!cancelled) setS({ _error: true }); });
    return () => { cancelled = true; };
  }, [api, childId, refreshKey]);

  const flagged = Number(s?.uncosted_count || 0) + Number(s?.unpriced_count || 0);
  const lowMargin = s && !s._error && Number(s.grand_total || 0) > 0 && Number(s.actual_margin_pct || 0) <= 0;
  const needsAck = flagged > 0 || lowMargin;
  const ok = !!s && !s._error && (!needsAck || ack);
  useEffect(() => { onOkChange?.(rowKey, ok); }, [ok, rowKey, onOkChange]);

  if (!s) return <div className="text-[11px] text-slate-400" data-testid={`wf-quote-cost-loading-${rowKey}`}>Checking costs…</div>;
  if (s._error) return <div className="text-[11px] text-rose-700" data-testid={`wf-quote-cost-error-${rowKey}`}>Could not load the cost check — reopen the review to retry.</div>;
  return (
    <div className="space-y-2" data-testid={`wf-quote-cost-check-${rowKey}`}>
      <div className="grid grid-cols-4 gap-2 text-[11px]">
        {[['Materials profit', s.materials_profit], ['Labour profit', s.labour_profit], ['Total profit', s.total_profit], ['Grand total ex GST', s.grand_total]].map(([k, v]) => (
          <div key={k} className="bg-slate-50 border border-slate-200 rounded p-1.5">
            <div className="text-slate-500">{k}</div>
            <div className={`font-bold font-mono ${Number(v) < 0 ? 'text-rose-700' : 'text-slate-800'}`}>{money(v)}</div>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-slate-600" data-testid={`wf-quote-cost-margin-${rowKey}`}>
        Margin <b>{Number(s.actual_margin_pct || 0).toFixed(1)}%</b> · markup {((Number(s.markup || 1) - 1) * 100).toFixed(1)}%
        {Number(s.discount_amount || 0) + Number(s.extra_discount_amount || 0) > 0 && <> · discounts {money(Number(s.discount_amount || 0) + Number(s.extra_discount_amount || 0))}</>}
      </div>
      {needsAck && (
        <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded p-2 space-y-1.5" data-testid={`wf-quote-cost-warning-${rowKey}`}>
          <div className="flex items-start gap-1.5 font-semibold">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>
              {flagged > 0 && `${flagged} line${flagged === 1 ? '' : 's'} with no buy-in / contractor cost or no price — the margin is overstated. `}
              {lowMargin && 'This quote makes no profit at the current pricing.'}
            </span>
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid={`wf-quote-cost-ack-${rowKey}`} />
            I've checked this and want to approve anyway
          </label>
        </div>
      )}
    </div>
  );
}
